import React from 'react';
import DatePicker from 'react-datepicker';
import PropTypes from 'prop-types';
import { FormFeedback } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import cn from 'utils/classnames';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './RankPromotionCalendar.module.scss';

const MAX_PROMOTION_DAYS = 3;
const MS_PER_DAY = 1000 * 60 * 60 * 24;

const getValidDate = (val) => {
  if (!val) return null;
  const date = new Date(val);
  return Number.isNaN(date.getTime()) ? null : date;
};

const normalizeDate = (val) => {
  const date = getValidDate(val);
  if (!date) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

const getDayDiff = (from, to) => Math.round((to.getTime() - from.getTime()) / MS_PER_DAY);

const formatWeekDay = (name) => {
  const cleanName = name.trim().toLowerCase();
  if (
    cleanName.startsWith('su') ||
    cleanName.includes('cn') ||
    cleanName.includes('sun')
  ) {
    return 'CN';
  }
  if (
    cleanName.startsWith('mo') ||
    cleanName.includes('t2') ||
    cleanName.includes('mon')
  ) {
    return 'T2';
  }
  if (
    cleanName.startsWith('tu') ||
    cleanName.includes('t3') ||
    cleanName.includes('tue')
  ) {
    return 'T3';
  }
  if (
    cleanName.startsWith('we') ||
    cleanName.includes('t4') ||
    cleanName.includes('wed')
  ) {
    return 'T4';
  }
  if (
    cleanName.startsWith('th') ||
    cleanName.includes('t5') ||
    cleanName.includes('thu')
  ) {
    return 'T5';
  }
  if (
    cleanName.startsWith('fr') ||
    cleanName.includes('t6') ||
    cleanName.includes('fri')
  ) {
    return 'T6';
  }
  if (
    cleanName.startsWith('sa') ||
    cleanName.includes('t7') ||
    cleanName.includes('sat')
  ) {
    return 'T7';
  }
  return name;
};

function RankPromotionCalendar({ validation, placePromotionList }) {
  const location = useLocation();
  const promotionId = new URLSearchParams(location.search).get('promotion');

  const [originalDates, setOriginalDates] = React.useState({
    start: null,
    end: null,
  });
  const [hoverDate, setHoverDate] = React.useState(null);

  React.useEffect(() => {
    if (
      promotionId &&
      !originalDates.start &&
      validation?.values?.bookingStartAt
    ) {
      setOriginalDates({
        start: getValidDate(validation.values.bookingStartAt),
        end: getValidDate(validation.values.bookingEndAt),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    promotionId,
    validation?.values?.bookingStartAt,
    validation?.values?.bookingEndAt,
  ]);

  const startDate = getValidDate(validation?.values?.bookingStartAt);
  const endDate = getValidDate(validation?.values?.bookingEndAt);

  const bookingStartAtError = validation?.errors?.bookingStartAt;
  const bookingEndAtError = validation?.errors?.bookingEndAt;
  const bookingDateErrorMessage = bookingEndAtError || bookingStartAtError;
  const showBookingDateError = bookingDateErrorMessage
    && validation?.submitCount > 0;

  const getDateState = React.useCallback(
    (date) => {
      if (!placePromotionList || !placePromotionList.length) return null;

      const cur = new Date(date);
      cur.setHours(0, 0, 0, 0);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let isAlreadyApplied = false;
      let isCooldown = false;

      placePromotionList.forEach((p) => {
        const isCurrentEditing = promotionId && (p._id === promotionId || p.id === promotionId);
        if (isCurrentEditing) return;

        const fromStr = p.bookingDate?.from;
        const toStr = p.bookingDate?.to;
        if (!fromStr || !toStr) return;

        const fromDate = new Date(fromStr);
        const toDate = new Date(toStr);
        fromDate.setHours(0, 0, 0, 0);
        toDate.setHours(0, 0, 0, 0);

        // Check if date is in the applied range
        if (cur >= fromDate && cur <= toDate) {
          isAlreadyApplied = true;
        }

        // Check if date is in the cooldown range
        const cooldownStart = new Date(toDate);
        cooldownStart.setDate(cooldownStart.getDate() + 1);

        const cooldownEnd = new Date(toDate);
        cooldownEnd.setDate(cooldownEnd.getDate() + 15);

        if (cur >= cooldownStart && cur <= cooldownEnd) {
          isCooldown = true;
        }
      });

      if (isAlreadyApplied) return 'applied';
      if (isCooldown) return 'cooldown';
      return null;
    },
    [placePromotionList, promotionId],
  );

  const isDateDisabled = React.useCallback(
    (date) => {
      const state = getDateState(date);

      const origStart = originalDates.start;
      const origEnd = originalDates.end;

      if (promotionId && origStart && origEnd) {
        const cur = new Date(date);
        cur.setHours(0, 0, 0, 0);
        const start = new Date(origStart);
        start.setHours(0, 0, 0, 0);
        const end = new Date(origEnd);
        end.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (today >= start) {
          // 1. Disable original dates [start, end]
          if (cur >= start && cur <= end) {
            return true;
          }
          // 2. Disable 15-day cooldown [end + 1, end + 15]
          const cooldownStart = new Date(end);
          cooldownStart.setDate(cooldownStart.getDate() + 1);
          const cooldownEnd = new Date(end);
          cooldownEnd.setDate(cooldownEnd.getDate() + 15);

          if (cur >= cooldownStart && cur <= cooldownEnd) {
            return true;
          }
        } else if (cur >= start && cur <= end) {
          // If today < start, allow original dates
          return false;
        }
      }

      return state === 'applied' || state === 'cooldown';
    },
    [getDateState, promotionId, originalDates],
  );

  const getDayClassName = React.useCallback(
    (date) => {
      const origStart = originalDates.start;
      const origEnd = originalDates.end;

      const cur = new Date(date);
      cur.setHours(0, 0, 0, 0);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let className = '';

      if (promotionId && origStart && origEnd) {
        const start = new Date(origStart);
        const end = new Date(origEnd);
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);

        if (cur >= start && cur <= end) {
          if (today >= start) {
            className = 'AlreadyAppliedDay';
          }
        } else if (today >= start) {
          const cooldownStart = new Date(end);
          cooldownStart.setDate(cooldownStart.getDate() + 1);
          const cooldownEnd = new Date(end);
          cooldownEnd.setDate(cooldownEnd.getDate() + 15);

          if (cur >= cooldownStart && cur <= cooldownEnd) {
            className = 'CannotCreateDay';
          }
        }
      }

      if (!className) {
        const state = getDateState(date);
        if (state === 'applied') {
          className = 'AlreadyAppliedDay';
        } else if (state === 'cooldown') {
          className = 'CannotCreateDay';
        }
      }

      // Cap selecting-range hover preview to max 3 consecutive days
      if (startDate && !endDate && hoverDate && !className) {
        const start = normalizeDate(startDate);
        const hover = normalizeDate(hoverDate);
        if (start && hover) {
          const hoverDiffDays = getDayDiff(start, hover);
          const curDiffDays = getDayDiff(start, cur);
          const selectingSpan = Math.abs(hoverDiffDays) + 1;

          if (selectingSpan > MAX_PROMOTION_DAYS) {
            const maxOffset = MAX_PROMOTION_DAYS - 1;
            const isBeyondAllowedWindow = hoverDiffDays >= 0
              ? curDiffDays > maxOffset && curDiffDays <= hoverDiffDays
              : curDiffDays < -maxOffset && curDiffDays >= hoverDiffDays;

            if (isBeyondAllowedWindow) {
              className = 'SuppressSelectingRange';
            }
          }
        }
      }

      return className;
    },
    [originalDates, promotionId, getDateState, startDate, endDate, hoverDate],
  );

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const onChange = React.useCallback(
    (dates) => {
      const [start, end] = dates;

      let finalStart = start;
      let finalEnd = end;

      if (start && end) {
        const diffTime = Math.abs(end.getTime() - start.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

        if (diffDays > MAX_PROMOTION_DAYS) {
          finalStart = end;
          finalEnd = null;
        } else {
          let hasDisabledDateInRange = false;
          const date1 = new Date(start);
          date1.setHours(0, 0, 0, 0);
          const date2 = new Date(end);
          date2.setHours(0, 0, 0, 0);

          if (isDateDisabled(date1) || isDateDisabled(date2)) {
            hasDisabledDateInRange = true;
          } else if (diffDays === MAX_PROMOTION_DAYS) {
            const middleDate = new Date(date1);
            middleDate.setDate(middleDate.getDate() + 1);
            if (isDateDisabled(middleDate)) {
              hasDisabledDateInRange = true;
            }
          }

          if (hasDisabledDateInRange) {
            finalStart = end;
            finalEnd = null;
          }
        }
      }

      setHoverDate(null);

      if (validation && validation.setFieldValue) {
        validation.setFieldValue('bookingStartAt', finalStart || '');
        validation.setFieldValue('bookingEndAt', finalEnd || '');
      }
    },
    [isDateDisabled, validation],
  );

  const onDayMouseEnter = React.useCallback((date) => {
    setHoverDate(date);
  }, []);

  const onCalendarMouseLeave = React.useCallback(() => {
    setHoverDate(null);
  }, []);

  const renderCustomHeader = React.useCallback(
    ({
      monthDate,
      decreaseMonth,
      increaseMonth,
      prevMonthButtonDisabled,
      nextMonthButtonDisabled,
      customHeaderCount,
    }) => {
      const monthNames = [
        'Tháng 1',
        'Tháng 2',
        'Tháng 3',
        'Tháng 4',
        'Tháng 5',
        'Tháng 6',
        'Tháng 7',
        'Tháng 8',
        'Tháng 9',
        'Tháng 10',
        'Tháng 11',
        'Tháng 12',
      ];
      const monthStr = monthNames[monthDate.getMonth()];
      const yearStr = monthDate.getFullYear();

      const showPrev = customHeaderCount === 0;
      const showNext = customHeaderCount === 1;

      return (
        <div className={styles.CustomHeader}>
          {showPrev && (
            <button
              onClick={decreaseMonth}
              disabled={prevMonthButtonDisabled}
              type="button"
              className={cn(styles.NavButton, styles.PrevButton)}
              style={{ display: prevMonthButtonDisabled ? 'none' : 'block' }}
            >
              «
            </button>
          )}
          <div className={styles.CurrentMonth}>{`${monthStr} ${yearStr}`}</div>
          {showNext && (
            <button
              onClick={increaseMonth}
              disabled={nextMonthButtonDisabled}
              type="button"
              className={cn(styles.NavButton, styles.NextButton)}
              style={{ display: nextMonthButtonDisabled ? 'none' : 'block' }}
            >
              »
            </button>
          )}
        </div>
      );
    },
    [],
  );

  return (
    <div className={styles.CalendarContainer}>
      <h3 className={styles.Title}>Khi nào khách có thể đặt ưu đãi này?</h3>

      <div
        className={cn(
          styles.CalendarWidget,
          showBookingDateError && styles.CalendarWidgetError,
        )}
      >
        <div
          className={styles.DatePickerWrapper}
          onMouseLeave={onCalendarMouseLeave}
        >
          <DatePicker
            selected={startDate}
            onChange={onChange}
            onDayMouseEnter={onDayMouseEnter}
            startDate={startDate}
            endDate={endDate}
            selectsRange
            inline
            monthsShown={2}
            minDate={today}
            showOutsideDays
            renderDayContents={(day, date) => date.getDate()}
            formatWeekDay={formatWeekDay}
            renderCustomHeader={renderCustomHeader}
            calendarClassName={styles.InlineCalendar}
            dayClassName={getDayClassName}
            filterDate={(date) => !isDateDisabled(date)}
          />
        </div>
        {showBookingDateError && (
          <FormFeedback
            type="invalid"
            className="d-block font-size-12 mt-0"
          >
            {bookingDateErrorMessage}
          </FormFeedback>
        )}
      </div>

      {/* Legend */}
      <div className={styles.LegendContainer}>
        <div className={styles.LegendItem}>
          <div className={cn(styles.LegendBox, styles.CannotCreate)}>1</div>
          <span>
            Ngày bạn
            {' '}
            <strong>không thể</strong>
            {' '}
            tạo thêm ưu đãi giới hạn thời gian
          </span>
        </div>
        <div className={styles.LegendItem}>
          <div className={cn(styles.LegendBox, styles.AlreadyApplied)}>1</div>
          <span>
            Ngày bạn
            {' '}
            <strong>đã có ưu đãi</strong>
            {' '}
            giới hạn thời gian đang áp dụng
          </span>
        </div>
        <div className={styles.LegendItem}>
          <div className={cn(styles.LegendBox, styles.Selectable)}>1</div>
          <span>
            Ngày bạn
            {' '}
            <strong>có thể</strong>
            {' '}
            tạo ưu đãi giới hạn thời gian
          </span>
        </div>
      </div>

      {/* Info Box */}
      <div className={styles.InfoBox}>
        <ul>
          <li>
            Bạn có thể chọn tối đa 3 ngày liên tiếp để chạy khuyến mãi thứ hạng.
          </li>
          <li>
            Sau khi tạo khuyến mãi thành công, bạn chỉ có thể tạo khuyến mãi thứ
            hạng tiếp theo sau 15 ngày.
          </li>
          <li>
            Bạn vẫn có thể chỉnh sửa hoặc xoá khuyến mãi trước ngày áp dụng.
          </li>
          <li>
            Nếu khuyến mãi đã bắt đầu chạy, việc xoá khuyến mãi sẽ được tính là
            {' '}
            đã sử dụng lượt và bạn cần chờ 15 ngày để tạo khuyến mãi mới.
          </li>
        </ul>
      </div>
    </div>
  );
}

RankPromotionCalendar.propTypes = {
  validation: PropTypes.instanceOf(Object),
  placePromotionList: PropTypes.instanceOf(Array),
};

RankPromotionCalendar.defaultProps = {
  validation: null,
  placePromotionList: [],
};

export default RankPromotionCalendar;
